import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { calcMaterials } from './MaterialCalculator';
/**
 * SimpleAreaCalculator
 * --------------------
 * A stripped-down area calculator used inline on the Product page. The
 * product context already tells us the MATERIAL — so we drop the
 * material / project-type pickers from the full MaterialCalculator and
 * just ask the shopper for shape + dimensions + depth.
 *
 * Live yardage updates as the user types. An "Apply" button hands the
 * computed yard count back to the parent (which then snaps the
 * quantity selector or splits multi-loads, exactly like the full
 * calculator did).
 */
type Shape = 'rect' | 'circle' | 'tri' | 'other';
type Props = {
  /** Material slug, used to look up density from calcMaterials. */
  materialSlug?: string;
  /** Fired when the shopper confirms — yard count is already rounded up. */
  onApply: (yards: number) => void;
};
export function SimpleAreaCalculator({
  materialSlug,
  onApply
}: Props) {
  const [shape, setShape] = useState<Shape>('rect');
  const [unit, setUnit] = useState<'ft' | 'm'>('ft');
  const [depthUnit, setDepthUnit] = useState<'in' | 'cm'>('in');
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [diameter, setDiameter] = useState('');
  const [base, setBase] = useState('');
  const [height, setHeight] = useState('');
  const [depth, setDepth] = useState('3');
  // Pull density from the shared materials registry so the tonnage /
  // yardage math matches the full calculator. Falls back to a 1.4
  // average if the slug isn't in the list.
  const density = useMemo(() => {
    const m = calcMaterials.find(x => x.id === materialSlug);
    return m ? m.density : 1.4;
  }, [materialSlug]);
  // Reset stale dimension state when the shape changes — keeps the
  // result from carrying over between shapes.
  useEffect(() => {
    setLength('');
    setWidth('');
    setDiameter('');
    setBase('');
    setHeight('');
  }, [shape]);
  const yards = useMemo(() => {
    const d = parseFloat(depth);
    if (isNaN(d) || d <= 0) return 0;
    const mToFt = 3.28084;
    const toFt = (v: string) => {
      const n = parseFloat(v) || 0;
      return unit === 'm' ? n * mToFt : n;
    };
    let areaSqFt = 0;
    if (shape === 'rect' || shape === 'other') {
      const l = toFt(length);
      const w = toFt(width);
      if (l <= 0 || w <= 0) return 0;
      areaSqFt = l * w;
    } else if (shape === 'circle') {
      const dia = toFt(diameter);
      if (dia <= 0) return 0;
      areaSqFt = Math.PI * (dia / 2) ** 2;
    } else if (shape === 'tri') {
      const b = toFt(base);
      const h = toFt(height);
      if (b <= 0 || h <= 0) return 0;
      areaSqFt = 0.5 * b * h;
    }
    const depthFt = depthUnit === 'cm' ? d / 30.48 : d / 12;
    const cubicYards = areaSqFt * depthFt / 27;
    return Math.ceil(cubicYards * 10) / 10;
  }, [shape, unit, depthUnit, length, width, diameter, base, height, depth]);
  const tons = Math.ceil(yards * density * 10) / 10;
  const canApply = yards > 0;
  const shapes: {
    id: Shape;
    label: string;
  }[] = [{
    id: 'rect',
    label: 'Rect'
  }, {
    id: 'circle',
    label: 'Circle'
  }, {
    id: 'tri',
    label: 'Tri'
  }, {
    id: 'other',
    label: 'Other'
  }];
  const inputCls = 'w-full rounded-md border border-stone-300 bg-white px-4 py-2.5 text-sm text-stone-900 outline-none transition-colors placeholder-stone-400 focus:border-[#04A05C] focus:ring-2 focus:ring-[#04A05C]/35 dark:border-[#404040] dark:bg-[#252525] dark:text-white';
  const labelCls = 'mb-1.5 block text-xs font-medium text-stone-600 dark:text-stone-300';
  return <div className="rounded-xl border border-stone-100 bg-[#fffff9] p-5 dark:border-[#4b4b4b] dark:bg-[#202020]" data-id="element-1645" data-mp="zqbvf5pgm1p9">
      <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_18rem] md:items-stretch" data-id="element-1646" data-mp="uriipfhzyeq5">
        <div className="min-w-0" data-id="element-1647" data-mp="2uthyf3cutnxl">
          <p className="mb-4 text-xs font-bold uppercase tracking-widest text-stone-600 dark:text-stone-300" data-id="element-1648" data-mp="2uujbtehcemih">
            Area Shape
          </p>

      {/* Matches the calculator used in the materials hub: a clear selected
            shape, with the remaining choices kept as outlined buttons. */}
      <div className="grid grid-cols-4 gap-2 mb-5" data-id="element-1649" data-mp="2q201qrhv69if">
        {shapes.map(({
            id,
            label
          }) => <button key={id} type="button" onClick={() => setShape(id)} className={`relative min-w-0 rounded-md border px-2 py-2.5 text-sm font-bold transition-colors ${shape === id ? 'border-[#04A05C] bg-[#04A05C] text-white' : 'border-stone-200 bg-[#ffffff] text-stone-700 hover:bg-stone-50 dark:border-[#404040] dark:bg-[#252525] dark:text-stone-300 dark:hover:bg-[#2f2f2f]'}`} data-id="element-1650" data-mp="2c2qs6ao5ydnf">
            {label}
          </button>)}
      </div>

      {/* Compact rectangular segmented controls match the materials-hub
            calculator and keep the unit choices legible at mobile widths. */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-3 mb-5 text-xs" data-id="element-1651" data-mp="1psgxvcxq4slc">
        <div className="flex items-center gap-2" data-id="element-1652" data-mp="1kxbi6vux0a5p">
          <span className="font-medium text-stone-500 dark:text-stone-400" data-id="element-1653" data-mp="1zkwkwonvo7d3">
            Units:
          </span>
          <div className="inline-flex overflow-hidden rounded-md border border-stone-200 dark:border-[#404040]" data-id="element-1654" data-mp="1utef9nawtils">
            {(['ft', 'm'] as const).map(u => <button key={u} type="button" onClick={() => setUnit(u)} className={`px-3 py-1.5 font-bold transition-colors ${unit === u ? 'bg-[#04A05C] text-white' : 'bg-white dark:bg-[#252525] text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-[#2f2f2f]'}`} data-id="element-1655" data-mp="23lzzz8y3pzd9">
                {u}
              </button>)}
          </div>
        </div>
        <div className="flex items-center gap-2" data-id="element-1656" data-mp="j76c54ltcux4">
          <span className="font-medium text-stone-500 dark:text-stone-400" data-id="element-1657" data-mp="j8hhv9ytkcqv">
            Depth:
          </span>
          <div className="inline-flex overflow-hidden rounded-md border border-stone-200 dark:border-[#404040]" data-id="element-1658" data-mp="1cvvm2xx550tc">
            {(['in', 'cm'] as const).map(u => <button key={u} type="button" onClick={() => setDepthUnit(u)} className={`px-3 py-1.5 font-bold transition-colors ${depthUnit === u ? 'bg-[#04A05C] text-white' : 'bg-white dark:bg-[#252525] text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-[#2f2f2f]'}`} data-id="element-1659" data-mp="1htnh40t3131r">
                {u}
              </button>)}
          </div>
        </div>
      </div>

      {/* Dimension inputs — change based on shape */}
      <AnimatePresence mode="wait" data-id="element-1660" data-mp="25mdorxtyd068">
        {(shape === 'rect' || shape === 'other') && <motion.div key="rect" initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -6
          }} className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-id="element-1661" data-mp="2ahjdf4eqosy0">
            <div data-id="element-1662" data-mp="3q3d8u3wy66n5">
              <label className={labelCls} data-id="element-1663" data-mp="3q2tvz1m5m8fz">Length ({unit})</label>
              <input type="number" value={length} onChange={e => setLength(e.target.value)} placeholder="0" className={inputCls} min="0" step="any" data-id="element-1664" data-mp="3l4k1kt897bvf" />
            </div>
            <div data-id="element-1665" data-mp="cv0auh0t5fw0">
              <label className={labelCls} data-id="element-1666" data-mp="cuh47wglydxa">Width ({unit})</label>
              <input type="number" value={width} onChange={e => setWidth(e.target.value)} placeholder="0" className={inputCls} min="0" step="any" data-id="element-1667" data-mp="7xictlobwl2s" />
            </div>
            <div data-id="element-1668" data-mp="8xji7delmkvw">
              <label className={labelCls} data-id="element-1669" data-mp="8x0bksuefix6">Depth ({depthUnit})</label>
              <input type="number" value={depth} onChange={e => setDepth(e.target.value)} placeholder="0" className={inputCls} min="0" step="any" data-id="element-1670" data-mp="dojecgfw8yxc" />
            </div>
          </motion.div>}
        {shape === 'circle' && <motion.div key="circ" initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -6
          }} className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-id="element-1671" data-mp="smtxiqafn3th">
            <div data-id="element-1672" data-mp="33d27go3pkfr9">
              <label className={labelCls} data-id="element-1673" data-mp="33ciod42bnlbn">Diameter ({unit})</label>
              <input type="number" value={diameter} onChange={e => setDiameter(e.target.value)} placeholder="0" className={inputCls} min="0" step="any" data-id="element-1674" data-mp="38dpyvavumym0" />
            </div>
            <div data-id="element-1675" data-mp="39uvil36jtjb0">
              <label className={labelCls} data-id="element-1676" data-mp="39tanncgbnycm">Depth ({depthUnit})</label>
              <input type="number" value={depth} onChange={e => setDepth(e.target.value)} placeholder="0" className={inputCls} min="0" step="any" data-id="element-1677" data-mp="34viqo40tlm1k" />
            </div>
          </motion.div>}
        {shape === 'tri' && <motion.div key="tri" initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -6
          }} className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-id="element-1678" data-mp="2qwsrmiufxcak">
            <div data-id="element-1679" data-mp="17n558qw7w8jr">
              <label className={labelCls} data-id="element-1680" data-mp="17nodffw2fikl">Base ({unit})</label>
              <input type="number" value={base} onChange={e => setBase(e.target.value)} placeholder="0" className={inputCls} min="0" step="any" data-id="element-1681" data-mp="12otumdy3lu0h" />
            </div>
            <div data-id="element-1682" data-mp="opu6r8ietj5d">
              <label className={labelCls} data-id="element-1683" data-mp="oqdoao45e5iv">Height ({unit})</label>
              <input type="number" value={height} onChange={e => setHeight(e.target.value)} placeholder="0" className={inputCls} min="0" step="any" data-id="element-1684" data-mp="jylt20a1rbme" />
            </div>
            <div data-id="element-1685" data-mp="5yuloie1cthd">
              <label className={labelCls} data-id="element-1686" data-mp="5zcef51hopkj">Depth ({depthUnit})</label>
              <input type="number" value={depth} onChange={e => setDepth(e.target.value)} placeholder="0" className={inputCls} min="0" step="any" data-id="element-1687" data-mp="60nht3r0vvb4" />
            </div>
          </motion.div>}
      </AnimatePresence>
        </div>

        <aside className="flex min-h-[14rem] flex-col border border-stone-200 bg-[#f4f7f2] p-5 dark:border-[#404040] dark:bg-[#252525]" data-id="element-1688" data-mp="2loaydlbvtxq4">
          <p className="text-xs font-bold uppercase tracking-widest text-stone-500 dark:text-stone-400" data-id="element-1689" data-mp="2qki3xd0d977j">Your estimate</p>
          <div className="flex flex-1 flex-col justify-center" data-id="element-1690" data-mp="1xxp20rb8q1vy">
            <AnimatePresence mode="wait" data-id="element-1691" data-mp="1xw49f78hkt0q">
              {canApply ? <motion.div key="result" initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -6
            }} data-id="element-1692" data-mp="22w8piw5hflqf">
                  <p className="font-heading text-4xl font-extrabold tracking-tight text-stone-950 dark:text-white" data-id="element-1693" data-mp="27pbcumjo1yy6">{yards} <span className="text-xl font-bold" data-id="element-1694" data-mp="1a6jlsb1e17l4">yd³</span></p>
                  <p className="mt-1 text-sm font-medium text-stone-600 dark:text-stone-300" data-id="element-1695" data-mp="1f6eduhwwg37r">Approximately {tons} tons of material</p>
                </motion.div> : <motion.p key="empty" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} className="max-w-[15rem] text-sm leading-relaxed text-stone-500 dark:text-stone-400" data-id="element-1696" data-mp="2djjo8xk3b8n6">Enter your project dimensions to see the material estimate.</motion.p>}
            </AnimatePresence>
          </div>
          <button type="button" onClick={() => onApply(yards)} disabled={!canApply} className="inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-[#04A05C] px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-[#078d53] disabled:cursor-not-allowed disabled:bg-stone-300 disabled:text-stone-500 dark:disabled:bg-white/10 dark:disabled:text-white/35 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#04A05C] focus-visible:ring-offset-2 dark:focus-visible:ring-offset-[#303030]" data-id="element-1697" data-mp="1zjh9hk72wvc5">
            {canApply ? `Use ${Math.ceil(yards)} yd` : 'Add dimensions'}
            <ArrowRight className="h-4 w-4" data-id="element-1698" data-mp="3nhjfsxnl7ute" />
          </button>
        </aside>
      </div>
    </div>;
}